import React from "react";
import type { CSSProperties } from "react";
import {
  resolveBackgroundImageUrl,
  resolveColor,
  resolveMaxWidth,
  resolveMinHeight,
  TEMPLATE_LINE_HEIGHT_BODY,
  TEMPLATE_LINE_HEIGHT_TIGHT,
  TEMPLATE_MAX_WIDTH_VAR,
  TEMPLATE_SPACING,
  TEMPLATE_TYPE_SCALE,
} from "@openings/template-components";
import { ServiceItemRow } from "./ServiceItemRow";
import { bookingMetaTextStyle, serviceItemRowStyles } from "./bookingTextStyles";

export type ServicesBookingSectionProps = {
  title?: string;
  subtitle?: string;
  note?: string;
  services: Array<{
    id: string;
    title: string;
    description?: string;
    price?: number;
    up?: boolean;
    duration?: number;
  }>;
  children?: React.ReactNode;
  style?: {
    backgroundColor?: string;
    backgroundImage?: string;
    backgroundImageUrl?: string;
    textColor?: string;
    minHeight?: number | string | null;
    maxWidth?: number | string | null;
  };
};

const SECTION_BACKGROUND_DEFAULT = "var(--openings-color-surface, #FFFFFF)";
const SECTION_TEXT_COLOR = "var(--openings-color-text, #111)";

const SECTION_STYLE: CSSProperties = {
  width: "100%",
  display: "flex",
  justifyContent: "center",
  boxSizing: "border-box",
};

const CONTENT_STYLE: CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: TEMPLATE_SPACING.lg,
  alignItems: "center",
};

const SECTION_CLASS = "openings-services-booking-section";
const BOOKING_CENTER_CLASS = "openings-services-booking-center";

const SERVICES_LIST_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: TEMPLATE_SPACING.md,
};

const SERVICE_ITEM_STYLE: CSSProperties = {
  padding: `0 0 ${TEMPLATE_SPACING.sm} 0`,
  borderBottom: "1px solid #f0f0f0",
};

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

export function ServicesBookingSection({
  title,
  subtitle,
  note,
  services,
  children,
  style: customStyle,
}: ServicesBookingSectionProps) {
  const displayTitle = title && title.trim().length ? title : null;
  const displaySubtitle = subtitle && subtitle.trim().length ? subtitle : null;
  const displayNote = note && note.trim().length ? note : null;
  const shouldRenderHeader = Boolean(displayTitle || displaySubtitle);

  const backgroundColor = resolveColor(
    customStyle?.backgroundColor,
    SECTION_BACKGROUND_DEFAULT,
  );
  const backgroundImageUrl = resolveBackgroundImageUrl(
    customStyle?.backgroundImageUrl ?? customStyle?.backgroundImage,
  );
  const textColor = resolveColor(customStyle?.textColor, SECTION_TEXT_COLOR);
  const sectionMinHeight =
    customStyle?.minHeight != null
      ? resolveMinHeight(customStyle.minHeight)
      : null;
  const sectionMaxWidth =
    customStyle?.maxWidth === undefined || customStyle?.maxWidth === null
      ? TEMPLATE_MAX_WIDTH_VAR
      : resolveMaxWidth(customStyle?.maxWidth);

  const hasServices = services.length > 0;

  return (
    <section
      className={SECTION_CLASS}
      style={{
        ...SECTION_STYLE,
        backgroundColor,
        backgroundImage: backgroundImageUrl
          ? `url(${backgroundImageUrl})`
          : undefined,
        backgroundSize: backgroundImageUrl ? "cover" : undefined,
        backgroundPosition: backgroundImageUrl ? "center" : undefined,
        minHeight: sectionMinHeight ? `${sectionMinHeight}px` : undefined,
      }}
    >
      <div
        style={{
          ...CONTENT_STYLE,
          maxWidth: sectionMaxWidth ?? undefined,
          color: textColor,
        }}
      >
        <style>{`
          .${SECTION_CLASS} {
            padding: ${TEMPLATE_SPACING.sm} ${TEMPLATE_SPACING.md};
          }

          .${BOOKING_CENTER_CLASS} {
            width: 100%;
            max-width: 480px;
            /* Booking-page B&W theme. A booking skin may restyle all three
               (EditorialBookingPageRoot emits the skin vars for skins only);
               every flat preset keeps the pinned B&W values. Surface and
               text must defer or a dark skin renders white controls on its
               canvas. */
            --openings-color-surface: var(--openings-booking-skin-surface, #fff);
            --openings-color-text: var(--openings-booking-skin-text, #111);
            --openings-color-border: var(--openings-booking-skin-control-border, #e5e5e5);
            color: var(--openings-color-text, #111);
          }

          @media (max-width: 768px) {
            .${SECTION_CLASS} {
              padding: ${TEMPLATE_SPACING.xs} ${TEMPLATE_SPACING.xs};
            }
          }
        `}</style>
        {/* Header */}
        {shouldRenderHeader ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {displayTitle ? (
              <h2
                style={{
                  margin: 0,
                  fontSize: TEMPLATE_TYPE_SCALE.h2,
                  lineHeight: TEMPLATE_LINE_HEIGHT_TIGHT,
                  fontWeight: 700,
                }}
              >
                {displayTitle}
              </h2>
            ) : null}
            {displaySubtitle ? (
              <p
                style={{
                  margin: 0,
                  fontSize: TEMPLATE_TYPE_SCALE.body,
                  lineHeight: TEMPLATE_LINE_HEIGHT_BODY,
                  ...bookingMetaTextStyle,
                }}
              >
                {displaySubtitle}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Centered booking UI */}
        {children ? (
          <div className={BOOKING_CENTER_CLASS}>{children}</div>
        ) : hasServices ? (
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={SERVICES_LIST_STYLE}>
              {services.map((service) => {
                const hasPrice =
                  typeof service.price === "number" && service.price > 0;
                const durationLabel =
                  service.duration !== undefined
                    ? formatMinutes(service.duration)
                    : null;

                return (
                  <div key={service.id} style={SERVICE_ITEM_STYLE}>
                    <ServiceItemRow
                      title={service.title}
                      price={service.price}
                      up={service.up}
                      durationLabel={durationLabel}
                      description={service.description}
                      nameStyle={serviceItemRowStyles.nameStyle}
                      priceStyle={serviceItemRowStyles.priceStyle}
                      durationStyle={{
                        ...serviceItemRowStyles.durationStyle,
                        ...(!hasPrice ? { fontStyle: "italic" } : {}),
                      }}
                      descriptionStyle={{
                        margin: 0,
                        ...serviceItemRowStyles.descriptionStyle,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Footer note */}
        {displayNote ? (
          <p
            style={{
              margin: 0,
              ...bookingMetaTextStyle,
            }}
          >
            {displayNote}
          </p>
        ) : null}
      </div>
    </section>
  );
}
